import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import "../index.css";
import { getAppointmentSlots, bookAppointment, getServices, findCustomerSuggestions, findVehicleSuggestions } from "../api";
import { useAlert } from "../AlertContext";
import {createPortal} from "react-dom";
import {toISODate} from "../utils";

function CustomerSummaryBadge({ customer }) {
    if (!customer) {
        return (
            <span className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-gray-200 text-gray-700">
                Ohne Kundenkonto
            </span>
        );
    }
    const label = customer.name || customer.email || customer.phone || "Bestehendes Kundenkonto";
    const details = [customer.email, customer.phone].filter(Boolean).join(" · ");
    return (
        <span className="inline-flex flex-col gap-0.5 px-3 py-1 rounded-full bg-blue-100 text-blue-800">
            <span className="font-medium">{label}</span>
            {details && <span className="text-xs text-blue-700">{details}</span>}
        </span>
    );
}

function VehicleSummaryBadge({ vehicle }) {
    if (!vehicle) {
        return (
            <span className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-gray-200 text-gray-700">
                Kein Fahrzeug verknüpft
            </span>
        );
    }
    const license = vehicle.licensePlate || "Unbekanntes Kennzeichen";
    if (vehicle.id) {
        return (
            <a
                href={`/vehicles/${vehicle.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition"
            >
                <span>{license}</span>
                <span className="text-xs text-blue-700">#{vehicle.id}</span>
            </a>
        );
    }
    return (
        <span className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800">
            {license}
        </span>
    );
}

function normalizeLicensePlateDisplay(value) {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value)
        .toUpperCase()
        // Erlaubt A–Z, 0–9, ÄÖÜ, Bindestrich und Leerzeichen
        .replace(/[^A-Z0-9ÄÖÜ\- ]+/g, "");
}

function normalizeLicensePlate(value) {
    if (value === null || value === undefined) {
        return "";
    }
    const upper = String(value).toUpperCase();
    let result = "";
    for (const char of upper) {
        const isDigit = char >= "0" && char <= "9";
        const isLetter = char.toLowerCase() !== char.toUpperCase();
        if (isLetter || isDigit) {
            result += char;
        }
    }
    return result;
}



function BookingForm() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [telefon, setTelefon] = useState("");
    const [kfz, setKfz] = useState("");
    const [normalizedKfz, setNormalizedKfz] = useState("");
    const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
    const [vehicleLookupAttempted, setVehicleLookupAttempted] = useState(false);
    const [vehicleLookupStatus, setVehicleLookupStatus] = useState("idle");
    const [vehicleLookupError, setVehicleLookupError] = useState("");
    const [vehicleChoice, setVehicleChoice] = useState("unknown");
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [serviceId, setServiceId] = useState("");
    const [services, setServices] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [slots, setSlots] = useState([]);
    const [honeypot, setHoneypot] = useState("");
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [createNewCustomer, setCreateNewCustomer] = useState(true);
    const [lookupAttempted, setLookupAttempted] = useState(false);
    const [lookupStatus, setLookupStatus] = useState("idle");
    const [lookupError, setLookupError] = useState("");
    const [submittedAppointment, setSubmittedAppointment] = useState(null);
    const [submittedVehicleLink, setSubmittedVehicleLink] = useState(null);
    const now = new Date();
    const todayStr = toISODate(now);
    const { showAlert } = useAlert();
    const previousNormalizedRef = useRef("");
    const selectedVehicle = useMemo(() => {
        if (!selectedVehicleId) {
            return null;
        }
        return vehicleSuggestions.find((item) => item.id === selectedVehicleId) || null;
    }, [selectedVehicleId, vehicleSuggestions]);

    const resetCustomerLookup = () => {
        setCustomerSuggestions([]);
        setSelectedCustomerId(null);
        setCreateNewCustomer(true);
        setLookupAttempted(false);
        setLookupStatus("idle");
        setLookupError("");
    };

    const resetVehicleLookup = useCallback(() => {
        setVehicleSuggestions([]);
        setVehicleLookupAttempted(false);
        setVehicleLookupStatus("idle");
        setVehicleLookupError("");
        setVehicleChoice("unknown");
        setSelectedVehicleId(null);
        previousNormalizedRef.current = "";
    }, []);

    useEffect(() => {
        getServices().then(data => setServices(Array.isArray(data) ? data : [])).catch(() => setServices([]));
    }, []);

    useEffect(() => {
        if (!selectedDate) return;
        getAppointmentSlots(selectedDate)
            .then((data) => setSlots(Array.isArray(data) ? data : []))
            .catch((err) => console.error(err));
    }, [selectedDate]);

    useEffect(() => {
        if (!normalizedKfz) {
            resetVehicleLookup();
            return;
        }

        setVehicleLookupStatus("loading");
        setVehicleLookupError("");
        const controller = new AbortController();
        const timer = setTimeout(() => {
            findVehicleSuggestions({ licensePlate: kfz || normalizedKfz }, { signal: controller.signal })
                .then((response) => {
                    if (controller.signal.aborted) {
                        return;
                    }
                    const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
                    setVehicleSuggestions(suggestions);
                    if (suggestions.length > 0) {
                        setVehicleLookupStatus("loaded");
                    } else {
                        setVehicleLookupStatus("empty");
                    }
                    if (response && typeof response.normalizedLicensePlate === "string") {
                        const normalizedValue = normalizeLicensePlate(response.normalizedLicensePlate);
                        if (normalizedValue && normalizedValue !== normalizedKfz) {
                            previousNormalizedRef.current = normalizedValue;
                            setNormalizedKfz(normalizedValue);
                        }
                    }
                })
                .catch((error) => {
                    if (controller.signal.aborted) {
                        return;
                    }
                    console.error("Vehicle lookup failed", error);
                    setVehicleLookupStatus("error");
                    setVehicleLookupError("Der Fahrzeugabgleich ist derzeit nicht möglich.");
                    setVehicleSuggestions([]);
                });
        }, 300);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [normalizedKfz, kfz, resetVehicleLookup]);

    useEffect(() => {
        if (!vehicleLookupAttempted) {
            if (vehicleChoice !== "unknown") {
                setVehicleChoice("unknown");
            }
            if (selectedVehicleId !== null) {
                setSelectedVehicleId(null);
            }
            return;
        }

        if (vehicleChoice === "unknown") {
            if (vehicleLookupStatus === "loaded" && vehicleSuggestions.length > 0) {
                const first = vehicleSuggestions[0];
                if (first && first.id) {
                    setSelectedVehicleId(first.id);
                    setVehicleChoice("existing");
                } else {
                    setVehicleChoice("new");
                }
            } else if (vehicleLookupStatus === "empty" || vehicleLookupStatus === "error") {
                setVehicleChoice("new");
            }
            return;
        }

        if (vehicleChoice === "existing") {
            if (vehicleLookupStatus === "error" || vehicleLookupStatus === "empty" || (vehicleLookupStatus === "loaded" && vehicleSuggestions.length === 0)) {
                setVehicleChoice("new");
                setSelectedVehicleId(null);
                return;
            }
            if (vehicleLookupStatus === "loaded" && selectedVehicleId && !vehicleSuggestions.some((item) => item.id === selectedVehicleId)) {
                const first = vehicleSuggestions[0];
                if (first && first.id) {
                    setSelectedVehicleId(first.id);
                } else {
                    setVehicleChoice("new");
                    setSelectedVehicleId(null);
                }
            }
            return;
        }

        if (vehicleChoice === "new") {
            if (selectedVehicleId !== null) {
                setSelectedVehicleId(null);
            }
        }
    }, [vehicleLookupAttempted, vehicleLookupStatus, vehicleSuggestions, vehicleChoice, selectedVehicleId]);



    const handleCustomerLookup = async () => {
        const trimmedEmail = email.trim();
        const trimmedTelefon = telefon.trim();
        if (!trimmedEmail && !trimmedTelefon) {
            resetCustomerLookup();
            return;
        }
        setLookupAttempted(true);
        setLookupStatus("loading");
        setLookupError("");
        try {
            const response = await findCustomerSuggestions({
                email: trimmedEmail || null,
                telefon: trimmedTelefon || null,
            });
            const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
            setCustomerSuggestions(suggestions);
            if (suggestions.length > 0) {
                const existingSelection = suggestions.find((item) => item.id === selectedCustomerId);
                if (existingSelection) {
                    setSelectedCustomerId(existingSelection.id);
                } else {
                    setSelectedCustomerId(suggestions[0].id);
                }
                setCreateNewCustomer(false);
            } else {
                setSelectedCustomerId(null);
                setCreateNewCustomer(true);
            }
            setLookupStatus("loaded");
        } catch (error) {
            console.error("Customer lookup failed", error);
            setLookupStatus("error");
            setLookupError("Der Kundenabgleich ist derzeit nicht möglich.");
            setCustomerSuggestions([]);
            setSelectedCustomerId(null);
            setCreateNewCustomer(true);
        }
    };

    const handleVehicleInputChange = (event) => {
        const rawValue = event.target.value;
        const displayValue = normalizeLicensePlateDisplay(rawValue);
        setKfz(displayValue);
        const normalizedValue = normalizeLicensePlate(displayValue);
        setNormalizedKfz(normalizedValue);
        if (normalizedValue) {
            setVehicleLookupAttempted(true);
            if (previousNormalizedRef.current !== normalizedValue) {
                setVehicleChoice("unknown");
                setSelectedVehicleId(null);
                previousNormalizedRef.current = normalizedValue;
            }
        } else {
            previousNormalizedRef.current = "";
            resetVehicleLookup();
        }
    };

    const handleSelectExistingVehicle = (vehicle) => {
        if (!vehicle) {
            return;
        }
        const displayValue = normalizeLicensePlateDisplay(vehicle.licensePlate || kfz);
        if (displayValue) {
            const normalizedValue = normalizeLicensePlate(displayValue);
            previousNormalizedRef.current = normalizedValue;
            setKfz(displayValue);
            setNormalizedKfz(normalizedValue);
        }
        setSelectedVehicleId(vehicle.id || null);
        setVehicleChoice("existing");
        setVehicleLookupAttempted(true);
    };

    const handleSelectNewVehicle = () => {
        setSelectedVehicleId(null);
        setVehicleChoice("new");
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (honeypot) {
            showAlert("Spam erkannt! Formular wird nicht gesendet.");
            return;
        }
        if (!selectedTime || !selectedDate) {
            showAlert("Bitte Datum und Uhrzeit auswählen!");
            return;
        }
        const day = new Date(selectedDate).getDay();
        if (day === 0 || day === 6) {
            showAlert("Samstage und Sonntage sind nicht buchbar.");
            return;
        }

        if (selectedDate < todayStr) {
            showAlert("Datum liegt in der Vergangenheit.");
            return;
        }
        if (selectedDate === todayStr && selectedTime < currentHHmm) {
            showAlert("Uhrzeit liegt in der Vergangenheit.");
            return;
        }

        const normalizedLicense = normalizeLicensePlate(kfz);
        const vehicleCandidatesSnapshot = vehicleSuggestions.map((item) => ({ ...item }));
        const selectedVehicleSnapshot = selectedVehicleId
            ? vehicleCandidatesSnapshot.find((item) => item.id === selectedVehicleId) || null
            : null;
        const effectiveVehicleChoice = normalizedLicense
            && vehicleChoice === "existing"
            && selectedVehicleSnapshot
            ? "existing"
            : "new";

        const payload = {
            name,
            email,
            telefon,
            kfz,
            serviceId,
            date: selectedDate,
            time: selectedTime,
            createNewCustomer,
        };
        if (selectedCustomerId) {
            payload.customerId = selectedCustomerId;
        }

        bookAppointment(payload)
            .then((result) => {
                const resolvedResult = result && typeof result === "object" ? result : null;
                if (resolvedResult) {
                    setSubmittedAppointment(resolvedResult);
                    setSubmittedVehicleLink({
                        choice: effectiveVehicleChoice,
                        selectedVehicle: selectedVehicleSnapshot,
                        suggestions: vehicleCandidatesSnapshot,
                        resultVehicle: resolvedResult.vehicle || null,
                        normalizedInput: normalizedLicense,
                        displayLicense: kfz,
                    });
                } else {
                    setSubmittedAppointment(null);
                    setSubmittedVehicleLink(null);
                }
                setIsModalOpen(false);

                setName("");
                setEmail("");
                setTelefon("");
                setKfz("");
                setNormalizedKfz("");
                setServiceId("");
                setSelectedDate("");
                setSelectedTime("");
                setHoneypot("");
                setSlots([]);
                resetCustomerLookup();
                resetVehicleLookup();
            })
            .catch((err) => {
                const alertMessage = err?.message || "Termin konnte nicht angefragt werden.";
                showAlert(alertMessage);
                setSubmittedVehicleLink(null);
            });
    };


    const isToday = selectedDate && selectedDate === todayStr;
    const currentHHmm = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;

    const isPastTime = (hhmm) => {
        // true, wenn hhmm < aktuelle Uhrzeit
        return hhmm < currentHHmm;
    };

    const linkType = submittedAppointment?.customerLinkType
        ? submittedAppointment.customerLinkType
        : submittedAppointment?.customer
            ? "EXISTING_MATCHED"
            : "NONE";

    const linkTypeTexts = {
        EXISTING_SELECTED: "Wir haben Ihre Anfrage mit Ihrem bestehenden Kundenkonto verknüpft.",
        EXISTING_MATCHED: "Wir haben ein bestehendes Kundenkonto zu Ihren Kontaktdaten gefunden.",
        CREATED_NEW: "Wir haben ein neues Kundenkonto für Sie angelegt.",
        NONE: "Ihre Anfrage wurde ohne Kundenkonto gespeichert.",
    };

    const vehicleResult = submittedAppointment?.vehicle || submittedVehicleLink?.resultVehicle || null;
    const vehicleSummary = vehicleResult
        || submittedVehicleLink?.selectedVehicle
        || (submittedVehicleLink?.displayLicense
            ? { licensePlate: submittedVehicleLink.displayLicense }
            : null);

    let vehicleLinkType = "NONE";
    const resultVehicleId = vehicleResult?.id || null;
    if (resultVehicleId) {
        if (submittedVehicleLink?.selectedVehicle && submittedVehicleLink.selectedVehicle.id === resultVehicleId) {
            vehicleLinkType = "EXISTING_SELECTED";
        } else if (submittedVehicleLink?.suggestions?.some((item) => item.id === resultVehicleId)) {
            vehicleLinkType = "EXISTING_MATCHED";
        } else {
            vehicleLinkType = "CREATED_NEW";
        }
    } else if (vehicleResult) {
        vehicleLinkType = "CREATED_NEW";
    } else if (submittedVehicleLink) {
        if (submittedVehicleLink.choice === "existing" && submittedVehicleLink.selectedVehicle) {
            vehicleLinkType = "EXISTING_SELECTED";
        } else if (submittedVehicleLink.choice === "new") {
            vehicleLinkType = "CREATED_NEW";
        }
    }

    const vehicleLinkTexts = {
        EXISTING_SELECTED: "Wir haben Ihr bestehendes Fahrzeug übernommen.",
        EXISTING_MATCHED: "Wir haben ein vorhandenes Fahrzeug zu Ihrem Kennzeichen gefunden.",
        CREATED_NEW: "Wir haben ein neues Fahrzeug für Sie angelegt.",
        NONE: "Wir haben kein Fahrzeug verknüpft.",
    };

    const vehicleLinkBadges = {
        EXISTING_SELECTED: "Bestehendes Fahrzeug",
        EXISTING_MATCHED: "Automatisch verknüpft",
        CREATED_NEW: "Neues Fahrzeug",
        NONE: "Ohne Fahrzeug",
    };


    return (
        <section id="booking" className="p-8 bg-gray-50 overflow-visible">
            <h3 className="text-3xl font-bold mb-6 text-center">Termin anfragen</h3>
            <form
                onSubmit={handleSubmit}
                className="flex flex-col max-w-md mx-auto space-y-4 bg-white p-8 rounded-xl shadow-lg"
            >
                {/* Name */}
                <input
                    type="text"
                    placeholder="Ihr Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                />

                {/* E-Mail */}
                <input
                    type="email"
                    placeholder="Ihre E-Mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleCustomerLookup}
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                />

                <input
                    type="text"
                    placeholder="Ihre Telefonnummer (optional)"
                    value={telefon}
                    onChange={(e) => setTelefon(e.target.value)}
                    onBlur={handleCustomerLookup}
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />

                {lookupAttempted && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                        {lookupStatus === "loading" ? (
                            <p className="text-sm text-blue-700">Wir prüfen, ob bereits ein Kundenkonto existiert …</p>
                        ) : lookupStatus === "error" ? (
                            <>
                                <p className="text-sm text-red-600">{lookupError || "Der Kundenabgleich ist derzeit nicht möglich."}</p>
                                <label className="flex items-center gap-2 text-sm text-blue-900">
                                    <input
                                        type="checkbox"
                                        checked={createNewCustomer}
                                        onChange={(event) => setCreateNewCustomer(event.target.checked)}
                                    />
                                    <span>Neues Kundenkonto anlegen</span>
                                </label>
                            </>
                        ) : customerSuggestions.length > 0 ? (
                            <>
                                <p className="text-sm font-semibold text-blue-900">
                                    Bestehendes Kundenkonto gefunden – bitte Auswahl bestätigen:
                                </p>
                                <div className="space-y-2">
                                    {customerSuggestions.map((suggestion) => {
                                        const details = [suggestion.email, suggestion.phone]
                                            .filter(Boolean)
                                            .join(" · ");
                                        return (
                                            <label
                                                key={suggestion.id}
                                                className="flex items-start gap-2 text-sm text-blue-900 cursor-pointer"
                                            >
                                                <input
                                                    type="radio"
                                                    name="customer-choice"
                                                    value={suggestion.id}
                                                    checked={selectedCustomerId === suggestion.id}
                                                    onChange={() => {
                                                        setSelectedCustomerId(suggestion.id);
                                                        setCreateNewCustomer(false);
                                                    }}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <div className="font-medium">
                                                        {suggestion.displayName || suggestion.email || suggestion.phone || "Ohne Namen"}
                                                    </div>
                                                    {details && <div className="text-xs text-blue-700">{details}</div>}
                                                    {Array.isArray(suggestion.matches) && suggestion.matches.length > 0 && (
                                                        <div className="text-[11px] text-blue-600 mt-1">
                                                            Treffer über {suggestion.matches.join(" & ")}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                    <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="customer-choice"
                                            value="new"
                                            checked={!selectedCustomerId && createNewCustomer}
                                            onChange={() => {
                                                setSelectedCustomerId(null);
                                                setCreateNewCustomer(true);
                                            }}
                                        />
                                        <span>Neues Kundenkonto anlegen</span>
                                    </label>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-blue-900">Wir legen für Sie ein neues Kundenkonto an.</p>
                                <label className="flex items-center gap-2 text-sm text-blue-900">
                                    <input
                                        type="checkbox"
                                        checked={createNewCustomer}
                                        onChange={(event) => setCreateNewCustomer(event.target.checked)}
                                    />
                                    <span>Neues Kundenkonto anlegen</span>
                                </label>
                            </>
                        )}
                    </div>
                )}

                {/* KFZ */}
                <input
                    type="text"
                    placeholder="Ihr Kennzeichen"
                    value={kfz}
                    onChange={handleVehicleInputChange}
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                />

                {vehicleLookupAttempted && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                        {vehicleLookupStatus === "loading" || vehicleLookupStatus === "idle" ? (
                            <p className="text-sm text-blue-700">Wir prüfen Ihr Kennzeichen …</p>
                        ) : vehicleLookupStatus === "error" ? (
                            <>
                                <p className="text-sm text-red-600">{vehicleLookupError || "Der Fahrzeugabgleich ist derzeit nicht möglich."}</p>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSelectNewVehicle}
                                        className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                                            vehicleChoice === "new"
                                                ? "bg-blue-600 text-white shadow"
                                                : "bg-white/70 text-blue-800 hover:bg-white"
                                        }`}
                                    >
                                        Neues Fahrzeug anlegen
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {vehicleSuggestions.length > 0 ? (
                                    <>
                                        <p className="text-sm font-semibold text-blue-900">Bestehendes Fahrzeug gefunden:</p>
                                        <div className="space-y-2">
                                            {vehicleSuggestions.map((vehicle, index) => {
                                                const key = vehicle.id || `${vehicle.licensePlate || "vehicle"}-${index}`;
                                                const isActive = vehicleChoice === "existing" && selectedVehicleId === vehicle.id;
                                                const details = [vehicle.make, vehicle.model, vehicle.modelVariant]
                                                    .filter(Boolean)
                                                    .join(" · ");
                                                return (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        onClick={() => handleSelectExistingVehicle(vehicle)}
                                                        className={`w-full text-left rounded-lg border px-3 py-2 transition ${
                                                            isActive
                                                                ? "border-blue-500 bg-white shadow"
                                                                : "border-transparent bg-white/70 hover:border-blue-400"
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div>
                                                                <div className="font-medium text-blue-900">{vehicle.licensePlate || "Ohne Kennzeichen"}</div>
                                                                {details && <div className="text-xs text-blue-700">{details}</div>}
                                                                {vehicle.customerName && (
                                                                    <div className="text-xs text-blue-600 mt-1">Kunde: {vehicle.customerName}</div>
                                                                )}
                                                            </div>
                                                            <span className="text-xs font-semibold uppercase text-blue-600">
                                                                {isActive ? "Ausgewählt" : "Übernehmen"}
                                                            </span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-blue-900">Kein bestehendes Fahrzeug gefunden.</p>
                                )}
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={handleSelectNewVehicle}
                                        className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                                            vehicleChoice === "new"
                                                ? "bg-blue-600 text-white shadow"
                                                : "bg-white/70 text-blue-800 hover:bg-white"
                                        }`}
                                    >
                                        Neues Fahrzeug anlegen
                                    </button>
                                    {vehicleChoice === "existing" && selectedVehicle && (
                                        <span className="text-xs text-blue-700">
                                            Ausgewählt: {selectedVehicle.licensePlate || "Ohne Kennzeichen"}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-blue-700">
                                    {vehicleChoice === "existing" && selectedVehicle
                                        ? "Wir übernehmen das bestehende Fahrzeug für Ihre Anfrage."
                                        : vehicleChoice === "new" || vehicleLookupStatus === "empty"
                                            ? "Wir legen ein neues Fahrzeug für Sie an."
                                            : "Bitte wählen Sie, ob das gefundene Fahrzeug übernommen werden soll."}
                                </p>
                            </>
                        )}
                    </div>
                )}

                {/* Service Auswahl */}
                <select
                    value={serviceId}
                    onChange={(e) => setServiceId(e.target.value)}
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                >
                    <option value="">-- Bitte Service auswählen --</option>
                    {services.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                </select>

                {/* Datum */}
                <input
                    type="date"
                    value={selectedDate}
                    min={todayStr}                // <- keine Tage vor heute wählbar
                    onChange={(e) => {
                        const newDate = e.target.value;
                        const day = new Date(newDate).getDay();
                        if (day === 0 || day === 6) {
                            showAlert("Samstage und Sonntage sind nicht buchbar.");
                            e.target.value = "";
                            setSelectedDate("");
                            return;
                        }
                        setSelectedDate(newDate);
                        setIsModalOpen(true);
                    }}
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                />


                {/* Uhrzeit Anzeige */}
                <input
                    type="text"
                    value={selectedTime}
                    placeholder="Bitte Datum wählen"
                    readOnly
                    className="p-3 border border-gray-300 rounded-lg bg-gray-100"
                />

                {/* Honeypot */}
                <input
                    type="text"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="hidden"
                    autoComplete="off"
                />

                {/* Submit */}
                <button
                    type="submit"
                    className="bg-blue-500 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-blue-600 transition"
                >
                    Termin anfragen
                </button>
            </form>

            {submittedAppointment && (
                <div className="mt-6 space-y-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-blue-900">Anfrage übermittelt</span>
                        <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs text-blue-700">
                            {submittedAppointment.date} · {submittedAppointment.time}
                        </span>
                        <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs text-blue-700">
                            {vehicleLinkBadges[vehicleLinkType]}
                        </span>
                    </div>
                    <p className="text-sm text-blue-900">{linkTypeTexts[linkType] || linkTypeTexts.NONE}</p>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-blue-900">Kundenkonto:</span>
                        <CustomerSummaryBadge customer={submittedAppointment.customer} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-blue-900">Fahrzeug:</span>
                        <VehicleSummaryBadge vehicle={vehicleSummary} />
                    </div>
                    <p className="text-sm text-blue-900">{vehicleLinkTexts[vehicleLinkType] || vehicleLinkTexts.NONE}</p>
                </div>
            )}

            {/* Modal für Zeitslots */}
            {isModalOpen && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]"
                    onClick={(e) => {
                        // schließen nur, wenn außerhalb des Modal-Containers geklickt wird
                        if (e.target === e.currentTarget) {
                            setSelectedDate("");
                            setIsModalOpen(false);
                        }
                    }}
                >
                    <div className="bg-white p-6 rounded-xl shadow-lg max-w-sm w-full">
                        <h4 className="text-xl font-bold mb-4 text-center">Verfügbare Slots</h4>
                        <div className="grid grid-cols-3 gap-4">
                            {slots.map((slot) => {
                                const pastOnToday = isToday && isPastTime(slot.time);
                                const disabled = slot.booked || pastOnToday;

                                return (
                                    <button
                                        key={slot.time}
                                        onClick={() => {
                                            if (!disabled) {
                                                setSelectedTime(slot.time);
                                                setIsModalOpen(false);
                                            }
                                        }}
                                        disabled={disabled}
                                        className={`py-2 px-3 rounded transition ${
                                            disabled
                                                ? "bg-gray-400 text-white cursor-not-allowed"        // vergangen ODER belegt
                                                : selectedTime === slot.time
                                                    ? "bg-blue-700 text-white"
                                                    : "bg-blue-500 text-white hover:bg-blue-600"
                                        }`}
                                        title={pastOnToday ? "Zeit liegt in der Vergangenheit" : slot.booked ? "Bereits belegt" : ""}
                                    >
                                        {slot.time}
                                    </button>
                                );
                            })}

                        </div>
                        <button
                            onClick={() => {
                                setSelectedDate("")
                                setIsModalOpen(false)
                            }}
                            className="mt-4 text-sm text-gray-500 underline"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </section>
    );
}

export default BookingForm;
